import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { ARENA_SIZE } from '../../../utils/constants.js';
import { PALETTE, matte, colored, vary, seededRandom, fbm, noise2, jitter } from '../../Style.js';
import { Obstacle } from './Obstacle.js';

const HALF = ARENA_SIZE / 2;
const WORLD_SIZE = 1100;
const WATER_LEVEL = -1.2;

// Paths worn into the meadow: base to base, and out to the forward bases.
const PATHS = [
  [[-100, 0], [-60, 4], [-30, -3], [0, 0], [30, 3], [60, -4], [100, 0]],
  [[0, 0], [-3, -25], [2, -50], [0, -80]],
  [[0, 0], [3, 25], [-2, 50], [0, 80]]
];

function distanceToSegment(px, pz, ax, az, bx, bz) {
  const dx = bx - ax;
  const dz = bz - az;
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (pz - az) * dz) / (dx * dx + dz * dz)));
  return Math.hypot(px - (ax + dx * t), pz - (az + dz * t));
}

function pathDistance(x, z) {
  let best = Infinity;
  for (const path of PATHS) {
    for (let i = 0; i < path.length - 1; i++) {
      best = Math.min(best, distanceToSegment(x, z, path[i][0], path[i][1], path[i + 1][0], path[i + 1][1]));
    }
  }
  // Wobble the verge so it reads as trodden earth, not a ruler line
  return best + noise2(x * 0.15, z * 0.15) * 1.2;
}

function smoothstep(a, b, x) {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
}

function riverCenter(x) {
  return 175 + Math.sin(x / 70) * 22 + Math.sin(x / 23) * 5;
}

// Height of the land outside the arena walls. The arena itself stays flat
// because the server's movement and line of sight assume flat ground.
export function terrainHeight(x, z) {
  const edge = Math.max(Math.abs(x), Math.abs(z)) - HALF;
  if (edge < 2) return -0.4;

  const rise = smoothstep(2, 70, edge);
  let h = rise * (5 + fbm(x / 90, z / 90) * 12 + Math.max(0, edge - 20) * 0.06);

  // A rocky mountain range to the north, behind the default camera view
  const north = smoothstep(130, 360, -z);
  if (north > 0) {
    const ridge = 1 - Math.abs(noise2(x / 70, z / 70));
    const crags = fbm(x / 35 + 10, z / 35, 3);
    h += north * (60 + ridge * 90 + crags * 25) * (1 - smoothstep(380, 520, Math.abs(x)) * 0.5);
  }

  // A river winding past the south side
  const riverDist = Math.abs(z - riverCenter(x));
  const bank = smoothstep(7, 22, riverDist);
  h = h * bank + (WATER_LEVEL - 2.2) * (1 - bank);

  return h;
}

// The world around and beneath the fighting: sky, hills, river, woods, and
// the arena's own walled meadow.
export class Arena {
  constructor(scene) {
    this.scene = scene;
    this.rand = seededRandom(1337);
    this.boundary = [];
    this.lanterns = [];
    this.windMaterials = [];
    this.uniforms = { uTime: { value: 0 } };
    this.create();
  }

  create() {
    this.createSky();
    this.createPlayfield();
    this.createTerrain();
    this.createWater();
    this.createWalls();
    this.createVegetation();
    this.createClouds();
    this.createFireflies();
  }

  createSky() {
    this.skyUniforms = {
      topColor: { value: new THREE.Color(0x4f8fd6) },
      horizonColor: { value: new THREE.Color(0xcfe3f0) },
      bottomColor: { value: new THREE.Color(0x8a8f7a) },
      sunDirection: { value: new THREE.Vector3(0, 1, 0) },
      sunColor: { value: new THREE.Color(0xfff1d6) },
      night: { value: 0 }
    };

    const sky = new THREE.Mesh(
      new THREE.SphereGeometry(1500, 32, 16),
      new THREE.ShaderMaterial({
        uniforms: this.skyUniforms,
        side: THREE.BackSide,
        depthWrite: false,
        fog: false,
        vertexShader: /* glsl */`
          varying vec3 vDir;
          void main() {
            vDir = normalize(position);
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
          }
        `,
        fragmentShader: /* glsl */`
          uniform vec3 topColor;
          uniform vec3 horizonColor;
          uniform vec3 bottomColor;
          uniform vec3 sunDirection;
          uniform vec3 sunColor;
          uniform float night;
          varying vec3 vDir;

          float hash(vec3 p) {
            return fract(sin(dot(p, vec3(12.9898, 78.233, 45.164))) * 43758.5453);
          }

          void main() {
            float h = vDir.y;
            vec3 col = mix(horizonColor, topColor, pow(smoothstep(0.0, 0.6, h), 0.7));
            col = mix(col, bottomColor, smoothstep(0.0, -0.25, h));

            // Soft sun glow and disc (a pale moon at night)
            float d = max(dot(vDir, sunDirection), 0.0);
            col += sunColor * pow(d, 64.0) * 0.6 * (1.0 - night * 0.6);
            col += sunColor * smoothstep(0.9993, 0.9996, d) * 1.5;

            // Stars come out at night
            vec3 cell = floor(vDir * 400.0);
            float star = step(0.9975, hash(cell)) * smoothstep(0.05, 0.3, h);
            col += vec3(star) * night * 0.9;

            gl_FragColor = vec4(col, 1.0);
            #include <tonemapping_fragment>
            #include <colorspace_fragment>
          }
        `
      })
    );
    sky.renderOrder = -1;
    sky.frustumCulled = false;
    this.sky = sky;
    this.scene.add(sky);
  }

  // The flat meadow inside the walls, coloured with grass, clover and worn paths.
  createPlayfield() {
    const size = ARENA_SIZE + 4;
    const geometry = new THREE.PlaneGeometry(size, size, 120, 120);
    geometry.rotateX(-Math.PI / 2);
    const pos = geometry.attributes.position;
    const colors = new Float32Array(pos.count * 3);

    const grass = new THREE.Color(PALETTE.grass);
    const grassDark = new THREE.Color(PALETTE.grassDark);
    const grassLight = new THREE.Color(PALETTE.grassLight);
    const dirt = new THREE.Color(PALETTE.dirt);
    const dirtDark = new THREE.Color(PALETTE.dirtDark);
    const c = new THREE.Color();

    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i);
      const z = pos.getZ(i);
      const n = fbm(x / 18, z / 18);
      c.copy(grass).lerp(n > 0 ? grassLight : grassDark, Math.min(1, Math.abs(n) * 1.6));

      const path = 1 - smoothstep(2.2, 4.5, pathDistance(x, z));
      if (path > 0) {
        const earth = dirt.clone().lerp(dirtDark, noise2(x * 0.4, z * 0.4) * 0.5 + 0.5);
        c.lerp(earth, path);
      }
      colors[i * 3] = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;
    }
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const ground = new THREE.Mesh(geometry, new THREE.MeshStandardMaterial({
      vertexColors: true,
      roughness: 1,
      metalness: 0
    }));
    ground.receiveShadow = true;
    this.ground = ground;
    this.scene.add(ground);
  }

  // Rolling hills outside the arena, rising into the northern mountains.
  createTerrain() {
    const segments = 150;
    const geometry = new THREE.PlaneGeometry(WORLD_SIZE, WORLD_SIZE, segments, segments);
    geometry.rotateX(-Math.PI / 2);
    const pos = geometry.attributes.position;

    for (let i = 0; i < pos.count; i++) {
      pos.setY(i, terrainHeight(pos.getX(i), pos.getZ(i)));
    }

    // Flat-shaded facets: each triangle gets one colour from its height and slope
    const flat = geometry.toNonIndexed();
    flat.computeVertexNormals();
    const fpos = flat.attributes.position;
    const normals = flat.attributes.normal;
    const colors = new Float32Array(fpos.count * 3);
    const rand = seededRandom(7);
    const c = new THREE.Color();

    for (let i = 0; i < fpos.count; i += 3) {
      const cx = (fpos.getX(i) + fpos.getX(i + 1) + fpos.getX(i + 2)) / 3;
      const cy = (fpos.getY(i) + fpos.getY(i + 1) + fpos.getY(i + 2)) / 3;
      const cz = (fpos.getZ(i) + fpos.getZ(i + 1) + fpos.getZ(i + 2)) / 3;
      const steep = 1 - normals.getY(i);

      if (cy < WATER_LEVEL + 0.6) {
        c.set(PALETTE.sand);
      } else if (cy > 105 + noise2(cx / 30, cz / 30) * 15) {
        c.set(PALETTE.snow);
      } else if (steep > 0.28 || cy > 70) {
        c.set(rand() < 0.5 ? PALETTE.rock : PALETTE.stoneDark);
      } else {
        const n = fbm(cx / 40, cz / 40);
        c.set(PALETTE.grass).lerp(new THREE.Color(n > 0 ? PALETTE.grassLight : PALETTE.foliageDark), Math.min(1, Math.abs(n) * 1.4));
        if (steep > 0.15) c.lerp(new THREE.Color(PALETTE.mossStone), 0.5);
      }
      c.offsetHSL(0, 0, (rand() - 0.5) * 0.04);
      for (let k = 0; k < 3; k++) {
        colors[(i + k) * 3] = c.r;
        colors[(i + k) * 3 + 1] = c.g;
        colors[(i + k) * 3 + 2] = c.b;
      }
    }
    flat.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const terrain = new THREE.Mesh(flat, new THREE.MeshStandardMaterial({
      vertexColors: true,
      flatShading: true,
      roughness: 1,
      metalness: 0
    }));
    terrain.receiveShadow = true;
    this.scene.add(terrain);
  }

  createWater() {
    const geometry = new THREE.PlaneGeometry(WORLD_SIZE, 80, 220, 16);
    geometry.rotateX(-Math.PI / 2);
    const pos = geometry.attributes.position;
    // Bend the strip to follow the river's course
    for (let i = 0; i < pos.count; i++) {
      pos.setZ(i, pos.getZ(i) + riverCenter(pos.getX(i)));
    }

    const material = new THREE.MeshStandardMaterial({
      color: PALETTE.water,
      roughness: 0.15,
      metalness: 0.1,
      transparent: true,
      opacity: 0.85,
      flatShading: true
    });
    this.addWind(material, /* glsl */`
      transformed.y += sin(uTime * 1.3 + position.x * 0.15) * 0.12 + cos(uTime * 1.1 + position.z * 0.2) * 0.1;
    `);

    const water = new THREE.Mesh(geometry, material);
    water.position.y = WATER_LEVEL;
    this.scene.add(water);
  }

  // Curtain walls and corner towers on the same footprint as the old boundary.
  createWalls() {
    const height = 5;
    const thickness = 1.5;
    const sides = [
      { id: 'boundary_n', position: { x: 0, y: 0, z: -HALF - thickness / 2 }, size: { x: ARENA_SIZE + 3, y: height, z: thickness } },
      { id: 'boundary_s', position: { x: 0, y: 0, z: HALF + thickness / 2 }, size: { x: ARENA_SIZE + 3, y: height, z: thickness } },
      { id: 'boundary_e', position: { x: HALF + thickness / 2, y: 0, z: 0 }, size: { x: thickness, y: height, z: ARENA_SIZE } },
      { id: 'boundary_w', position: { x: -HALF - thickness / 2, y: 0, z: 0 }, size: { x: thickness, y: height, z: ARENA_SIZE } }
    ];
    for (const side of sides) {
      this.boundary.push(new Obstacle(this.scene, { ...side, type: 'wall', rotation: 0 }));
    }

    // Corner towers: wide braziered columns, each with a warm light for the evening
    for (const [x, z] of [[-1, -1], [1, -1], [-1, 1], [1, 1]]) {
      const tower = new Obstacle(this.scene, {
        id: `tower_${x}_${z}`,
        type: 'pillar',
        position: { x: x * (HALF + 1), y: 0, z: z * (HALF + 1) },
        size: { x: 6, y: 9, z: 6 },
        rotation: 0
      });
      this.boundary.push(tower);
      const light = new THREE.PointLight(PALETTE.lanternGlow, 0, 45, 1.6);
      light.position.set(x * (HALF - 2), 11, z * (HALF - 2));
      this.scene.add(light);
      this.lanterns.push({ light, peak: 160 });
    }

    // Hanging lanterns along the parapet, which glow as evening falls
    const lanternGlass = new THREE.MeshStandardMaterial({
      color: 0xffe2a8,
      emissive: PALETTE.lanternGlow,
      emissiveIntensity: 0,
      roughness: 0.5,
      flatShading: true
    });
    this.lanternGlass = lanternGlass;
    const frames = [];
    const glassPositions = [];
    for (let along = -HALF + 12; along <= HALF - 12; along += 16) {
      for (const [x, z, ry] of [
        [along, -HALF + 0.2, 0], [along, HALF - 0.2, Math.PI],
        [HALF - 0.2, along, -Math.PI / 2], [-HALF + 0.2, along, Math.PI / 2]
      ]) {
        const inward = new THREE.Vector3(0, 0, 1).applyAxisAngle(new THREE.Vector3(0, 1, 0), ry);
        const bx = x + inward.x * 0.6;
        const bz = z + inward.z * 0.6;
        frames.push(colored(new THREE.BoxGeometry(0.15, 0.15, 1.1), PALETTE.iron, { x: x + inward.x * 0.3, y: 4.3, z: z + inward.z * 0.3, ry }));
        frames.push(colored(new THREE.ConeGeometry(0.4, 0.35, 4), PALETTE.iron, { x: bx, y: 4.15, z: bz, ry: Math.PI / 4 }));
        glassPositions.push([bx, 3.7, bz]);
      }
    }
    const frameMesh = new THREE.Mesh(mergeGeometries(frames), matte(0xffffff, { vertexColors: true }));
    this.scene.add(frameMesh);

    const glass = new THREE.InstancedMesh(new THREE.BoxGeometry(0.45, 0.6, 0.45), lanternGlass, glassPositions.length);
    const m = new THREE.Matrix4();
    glassPositions.forEach(([x, y, z], i) => glass.setMatrixAt(i, m.makeTranslation(x, y, z)));
    this.scene.add(glass);
  }

  // Instanced woods, rocks, bushes, grass and flowers.
  createVegetation() {
    const rand = this.rand;

    const deciduous = mergeGeometries([
      colored(new THREE.CylinderGeometry(0.35, 0.55, 3, 6), PALETTE.woodDark, { y: 1.5 }),
      colored(jitter(new THREE.IcosahedronGeometry(2.4, 0), 0.6, rand), PALETTE.foliage, { y: 4.4 }),
      colored(jitter(new THREE.IcosahedronGeometry(1.7, 0), 0.5, rand), PALETTE.grassLight, { x: 0.9, y: 5.6, z: 0.4 }),
      colored(jitter(new THREE.IcosahedronGeometry(1.5, 0), 0.5, rand), PALETTE.foliageDark, { x: -1.1, y: 4.9, z: -0.5 })
    ]);
    const pine = mergeGeometries([
      colored(new THREE.CylinderGeometry(0.25, 0.4, 2, 5), PALETTE.woodDark, { y: 1 }),
      colored(new THREE.ConeGeometry(2.2, 3.2, 7), PALETTE.pine, { y: 3.2 }),
      colored(new THREE.ConeGeometry(1.7, 2.8, 7), PALETTE.pine, { y: 4.8, ry: 0.4 }),
      colored(new THREE.ConeGeometry(1.1, 2.3, 7), PALETTE.pine, { y: 6.3, ry: 0.8 })
    ]);
    const rock = colored(jitter(new THREE.DodecahedronGeometry(1, 0), 0.5, rand), 0xffffff, { y: 0.3, scale: [1.3, 0.8, 1] });
    const bush = mergeGeometries([
      colored(jitter(new THREE.IcosahedronGeometry(1, 0), 0.35, rand), PALETTE.foliage, { y: 0.6 }),
      colored(jitter(new THREE.IcosahedronGeometry(0.8, 0), 0.3, rand), PALETTE.foliageDark, { x: 0.8, y: 0.5, z: 0.3 })
    ]);

    const scatter = (geometry, count, accept, { minScale = 0.8, maxScale = 1.4, tint, shadows = true }) => {
      const material = matte(0xffffff, { vertexColors: true });
      const mesh = new THREE.InstancedMesh(geometry, material, count);
      const dummy = new THREE.Object3D();
      let placed = 0;
      for (let attempt = 0; placed < count && attempt < count * 30; attempt++) {
        const x = (rand() - 0.5) * WORLD_SIZE * 0.9;
        const z = (rand() - 0.5) * WORLD_SIZE * 0.9;
        const h = terrainHeight(x, z);
        if (!accept(x, z, h)) continue;
        const s = minScale + rand() * (maxScale - minScale);
        dummy.position.set(x, h - 0.2, z);
        dummy.rotation.set((rand() - 0.5) * 0.15, rand() * Math.PI * 2, (rand() - 0.5) * 0.15);
        dummy.scale.set(s, s * (0.85 + rand() * 0.3), s);
        dummy.updateMatrix();
        mesh.setMatrixAt(placed, dummy.matrix);
        if (tint) mesh.setColorAt(placed, tint(x, z, h));
        placed++;
      }
      mesh.count = placed;
      mesh.castShadow = shadows;
      mesh.receiveShadow = true;
      this.scene.add(mesh);
      return mesh;
    };

    const outside = (x, z, margin) => Math.max(Math.abs(x), Math.abs(z)) > HALF + margin;
    const dry = (x, z) => Math.abs(z - riverCenter(x)) > 16;

    const autumn = new THREE.Color(PALETTE.foliageAutumn);
    scatter(deciduous, 700, (x, z, h) => outside(x, z, 9) && dry(x, z) && h < 35 && fbm(x / 60, z / 60) > -0.1, {
      tint: () => (rand() < 0.18 ? autumn.clone().offsetHSL(0, 0, (rand() - 0.5) * 0.1) : vary(0xffffff, rand, 0.2, 0.05))
    });
    scatter(pine, 1100, (x, z, h) => outside(x, z, 9) && dry(x, z) && h > 12 && h < 95, {
      minScale: 0.9, maxScale: 1.8, tint: () => vary(0xffffff, rand, 0.2)
    });
    scatter(rock, 450, (x, z, h) => outside(x, z, 5) && h > WATER_LEVEL, {
      minScale: 0.6, maxScale: 3.2,
      tint: (x, z, h) => vary(h > 60 ? PALETTE.stoneDark : PALETTE.rock, rand, 0.15)
    });
    scatter(bush, 600, (x, z, h) => outside(x, z, 4) && dry(x, z) && h < 40, {
      minScale: 0.7, maxScale: 1.5, tint: () => vary(0xffffff, rand, 0.2)
    });

    this.createGrass();
  }

  // Swaying grass tufts and wildflowers, inside the arena and across the hills.
  createGrass() {
    const rand = this.rand;
    // Each blade is two opposite-facing triangles rather than a double-sided
    // material, which would flip the upward normals on the back face
    const blade = (angle, lean) => {
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute([
        -0.09, 0, 0, 0.09, 0, 0, lean, 0.75, 0,
        0.09, 0, 0, -0.09, 0, 0, lean, 0.75, 0
      ], 3));
      g.computeVertexNormals();
      g.rotateY(angle);
      return g;
    };
    const tuft = mergeGeometries([blade(0, 0.1), blade(Math.PI / 3, -0.12), blade(-Math.PI / 3, 0.05), blade(Math.PI / 2, 0)]);
    tuft.computeVertexNormals();
    // Point blade normals up so tufts are lit like the ground beneath them
    const normals = tuft.attributes.normal;
    for (let i = 0; i < normals.count; i++) normals.setXYZ(i, 0, 1, 0);

    const grassMaterial = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 1 });
    this.addWind(grassMaterial, /* glsl */`
      vec3 root = instanceMatrix[3].xyz;
      float sway = sin(uTime * 1.8 + root.x * 0.35 + root.z * 0.25) + sin(uTime * 3.1 + root.z * 0.6) * 0.35;
      transformed.x += sway * position.y * 0.18;
      transformed.z += sway * position.y * 0.08;
    `);

    const count = 14000;
    const grass = new THREE.InstancedMesh(tuft, grassMaterial, count);
    const dummy = new THREE.Object3D();
    const colorA = new THREE.Color(PALETTE.grassLight);
    const colorB = new THREE.Color(PALETTE.grassDark);
    let placed = 0;
    for (let attempt = 0; placed < count && attempt < count * 6; attempt++) {
      // Two thirds inside the walls, the rest on the nearby hills
      const inside = placed < count * 0.66;
      const span = inside ? ARENA_SIZE - 4 : 420;
      const x = (rand() - 0.5) * span;
      const z = (rand() - 0.5) * span;
      let y = 0;
      if (inside) {
        if (pathDistance(x, z) < 4.5) continue;
      } else {
        if (Math.max(Math.abs(x), Math.abs(z)) < HALF + 4) continue;
        y = terrainHeight(x, z);
        if (y < WATER_LEVEL + 0.5 || y > 40) continue;
      }
      // Clumpy distribution
      if (fbm(x / 12, z / 12) < -0.15) continue;
      const s = 0.8 + rand() * 0.9;
      dummy.position.set(x, y, z);
      dummy.rotation.set(0, rand() * Math.PI * 2, 0);
      dummy.scale.set(s, s * (0.7 + rand() * 0.8), s);
      dummy.updateMatrix();
      grass.setMatrixAt(placed, dummy.matrix);
      grass.setColorAt(placed, colorA.clone().lerp(colorB, rand()));
      placed++;
    }
    grass.count = placed;
    grass.receiveShadow = true;
    this.scene.add(grass);

    // Wildflowers: a stalk with a coloured head
    const flower = mergeGeometries([
      colored(new THREE.CylinderGeometry(0.03, 0.03, 0.5, 3), PALETTE.grassDark, { y: 0.25 }),
      colored(new THREE.IcosahedronGeometry(0.14, 0), 0xffffff, { y: 0.55 })
    ]);
    const flowerMaterial = matte(0xffffff, { vertexColors: true, unique: true });
    this.addWind(flowerMaterial, /* glsl */`
      vec3 root = instanceMatrix[3].xyz;
      transformed.x += sin(uTime * 1.8 + root.x * 0.35 + root.z * 0.25) * position.y * 0.15;
    `);
    const petals = [0xf4f1e6, 0xf2cf4a, 0xe58fb0, 0x8fa8f0, 0xe8743c];
    const flowerCount = 2200;
    const flowers = new THREE.InstancedMesh(flower, flowerMaterial, flowerCount);
    placed = 0;
    for (let attempt = 0; placed < flowerCount && attempt < flowerCount * 10; attempt++) {
      const inside = placed < flowerCount * 0.6;
      const span = inside ? ARENA_SIZE - 6 : 360;
      const x = (rand() - 0.5) * span;
      const z = (rand() - 0.5) * span;
      let y = 0;
      if (inside) {
        if (pathDistance(x, z) < 5) continue;
      } else {
        if (Math.max(Math.abs(x), Math.abs(z)) < HALF + 4) continue;
        y = terrainHeight(x, z);
        if (y < WATER_LEVEL + 0.5 || y > 30) continue;
      }
      // Flowers grow in drifts
      if (fbm(x / 20 + 50, z / 20) < 0.1) continue;
      dummy.position.set(x, y, z);
      dummy.rotation.set(0, rand() * Math.PI, 0);
      const s = 0.8 + rand() * 0.6;
      dummy.scale.set(s, s, s);
      dummy.updateMatrix();
      flowers.setMatrixAt(placed, dummy.matrix);
      flowers.setColorAt(placed, new THREE.Color(petals[Math.floor(noise2(x / 15, z / 15) * 2.5 + 2.5) % petals.length]));
      placed++;
    }
    flowers.count = placed;
    this.scene.add(flowers);
  }

  createClouds() {
    const rand = this.rand;
    const material = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 1, flatShading: true, transparent: true, opacity: 0.92 });
    this.cloudMaterial = material;
    this.clouds = [];
    for (let i = 0; i < 18; i++) {
      const puffs = [];
      const n = 3 + Math.floor(rand() * 4);
      for (let p = 0; p < n; p++) {
        puffs.push(colored(jitter(new THREE.IcosahedronGeometry(8 + rand() * 8, 0), 3, rand), 0xffffff, {
          x: (p - n / 2) * 10 + rand() * 4, y: rand() * 4, z: (rand() - 0.5) * 10
        }));
      }
      const cloud = new THREE.Mesh(mergeGeometries(puffs), material);
      cloud.scale.y = 0.55;
      cloud.position.set((rand() - 0.5) * 1000, 140 + rand() * 60, (rand() - 0.5) * 1000);
      cloud.userData.speed = 2 + rand() * 3;
      this.scene.add(cloud);
      this.clouds.push(cloud);
    }
  }

  createFireflies() {
    const rand = this.rand;
    const count = 260;
    const positions = new Float32Array(count * 3);
    this.fireflySeeds = [];
    for (let i = 0; i < count; i++) {
      // Hug the edges of the arena and the meadow beyond
      const angle = rand() * Math.PI * 2;
      const r = 60 + rand() * 120;
      const x = Math.cos(angle) * r;
      const z = Math.sin(angle) * r;
      const y = Math.max(0, terrainHeight(x, z)) + 0.6 + rand() * 2.5;
      this.fireflySeeds.push({ x, y, z, phase: rand() * 100 });
      positions.set([x, y, z], i * 3);
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    // Soft round glow rather than square points
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = 32;
    const ctx = canvas.getContext('2d');
    const gradient = ctx.createRadialGradient(16, 16, 0, 16, 16, 16);
    gradient.addColorStop(0, 'rgba(255,255,255,1)');
    gradient.addColorStop(0.3, 'rgba(255,255,255,0.6)');
    gradient.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, 32, 32);

    this.fireflies = new THREE.Points(geometry, new THREE.PointsMaterial({
      color: 0xffe07a,
      map: new THREE.CanvasTexture(canvas),
      size: 0.9,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    }));
    this.fireflies.frustumCulled = false;
    this.scene.add(this.fireflies);
  }

  // Shared time uniform for anything that moves in the wind.
  addWind(material, chunk) {
    material.onBeforeCompile = shader => {
      shader.uniforms.uTime = this.uniforms.uTime;
      shader.vertexShader = shader.vertexShader
        .replace('#include <common>', '#include <common>\nuniform float uTime;')
        .replace('#include <begin_vertex>', `#include <begin_vertex>\n${chunk}`);
    };
    material.customProgramCacheKey = () => chunk;
  }

  // Called each frame with the current time-of-day state from the Scene.
  update(deltaTime, elapsed, day, focus) {
    this.uniforms.uTime.value = elapsed;

    if (day) {
      this.skyUniforms.topColor.value.copy(day.skyTop);
      this.skyUniforms.horizonColor.value.copy(day.skyHorizon);
      this.skyUniforms.bottomColor.value.copy(day.fog);
      this.skyUniforms.sunDirection.value.copy(day.sunDirection);
      this.skyUniforms.sunColor.value.copy(day.sun);
      this.skyUniforms.night.value = day.night;
      this.cloudMaterial.color.copy(day.cloud);

      const glow = smoothstep(0.15, 0.7, day.night);
      const flicker = 0.9 + Math.sin(elapsed * 7.3) * 0.05 + Math.sin(elapsed * 13.1) * 0.05;
      this.lanternGlass.emissiveIntensity = 0.2 + glow * 2.5 * flicker;
      for (const { light, peak } of this.lanterns) light.intensity = glow * peak * flicker;
      this.fireflies.material.opacity = smoothstep(0.4, 0.9, day.night);
    }

    // The sky follows the camera so it never clips
    if (focus) this.sky.position.set(focus.x, 0, focus.z);

    for (const cloud of this.clouds) {
      cloud.position.x += cloud.userData.speed * deltaTime;
      if (cloud.position.x > 600) cloud.position.x = -600;
    }

    if (this.fireflies.material.opacity > 0) {
      const attr = this.fireflies.geometry.attributes.position;
      this.fireflySeeds.forEach((f, i) => {
        const t = elapsed * 0.5 + f.phase;
        attr.setXYZ(i, f.x + Math.sin(t) * 2, f.y + Math.sin(t * 1.7) * 0.6, f.z + Math.cos(t * 0.8) * 2);
      });
      attr.needsUpdate = true;
    }

    for (const piece of this.boundary) piece.update(deltaTime, elapsed);
  }

  getOccluders() {
    return this.boundary;
  }
}
